import * as React from 'react';

import Bee from '@beefree.io/sdk';

import {useResizeObserver} from 'shells/utils/hooks/use-resize-observer';

import {SizeWarning} from '../../email-marketing/bee-editor/size-warning';

import {
    CustomAddOnSelectModal,
    getHtmlCustomAddOnAddBlockContent,
    getHtmlCustomAddOnPlaceholder,
    FORM_ADD_ON_ID,
    SCHEDULER_ADD_ON_ID,
} from './custom-add-ons';
import type {CustomAddOn} from './custom-add-ons';
import {emptyLandingPageTemplate} from './empty-landing-page-template';

import styles from './bee-editor.module.css';

// Minimum width for the Bee editor to not collapse content selection sidebar
const EDITOR_MIN_WIDTH = 1025;

type Props = {
    beeToken: string;
    onChange: (beeContentJson: string) => any;
    beeBodyJson: string | null | undefined;
};

export function BeeEditor(props: Props) {
    const {beeToken} = props;

    const containerRef = React.useRef(null);

    const [width] = useResizeObserver(containerRef.current);
    const [shouldShowSizeWarning, setShouldShowSizeWarning] = React.useState<
        boolean | null | undefined
    >(undefined);

    const [customAddOnSelectHandler, setCustomAddOnSelectHandler] = React.useState<
        | {
              addOnType: CustomAddOn;
              resolve: (arg1?: any) => void;
              reject: () => void;
          }
        | null
        | undefined
    >(undefined);

    React.useEffect(() => {
        const bee = new Bee(JSON.parse(beeToken));

        const config = {
            uid: NutshellFrontendSettings.instanceId
                ? `instance_${NutshellFrontendSettings.instanceId}`
                : null,
            container: 'bee-plugin-container',
            trackChanges: true, // Needed for onChange function to always work
            contentDialog: {
                addOn: {
                    // @ts-expect-error
                    handler: (resolve, reject, args) => {
                        setCustomAddOnSelectHandler({
                            addOnType: args.contentDialogId,
                            resolve,
                            reject,
                        });
                    },
                },
            },
            addOns: [
                {
                    id: FORM_ADD_ON_ID,
                    // Override plain placeholder text
                    placeholder: getHtmlCustomAddOnAddBlockContent(FORM_ADD_ON_ID),
                },
                {
                    id: SCHEDULER_ADD_ON_ID,
                    // Override plain placeholder text
                    placeholder: getHtmlCustomAddOnAddBlockContent(SCHEDULER_ADD_ON_ID),
                },
            ],
            onChange: props.onChange,
        } as const;

        // @ts-expect-error
        bee.start(config, props.beeBodyJson || emptyLandingPageTemplate());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [beeToken]);

    React.useLayoutEffect(() => {
        setShouldShowSizeWarning(Boolean(width && width < EDITOR_MIN_WIDTH));
    }, [width]);

    return (
        <div ref={containerRef} className={styles['editor-container']}>
            {shouldShowSizeWarning && <SizeWarning />}
            <div id='bee-plugin-container' className={styles['bee-plugin-wrapper']} />
            {customAddOnSelectHandler ? (
                <CustomAddOnSelectModal
                    addOnType={customAddOnSelectHandler.addOnType}
                    reject={() => {
                        customAddOnSelectHandler.reject();
                        setCustomAddOnSelectHandler(undefined);
                    }}
                    resolve={(params: {name: string; value: string}) => {
                        customAddOnSelectHandler.resolve({
                            type: 'html',
                            value: {
                                html: getHtmlCustomAddOnPlaceholder(
                                    customAddOnSelectHandler.addOnType,
                                    params.name,
                                    params.value
                                ),
                            },
                        });
                        setCustomAddOnSelectHandler(undefined);
                    }}
                />
            ) : undefined}
        </div>
    );
}
